import { SnapshotCard } from '@ovComponents/3-pattern/snapshotCard';
import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import {
  SPOTLIGHT_TYPES,
  spotlightColorKeyToSnapshotTone,
} from '../utils/reconFlags.config';

const SPOTLIGHT_TITLE_SX = {
  fontWeight: 700,
  textTransform: 'uppercase',
} as const;

export type ReconFlagsSpotlightSummaryProps = {
  theme: Theme;
  kpiCounts: Record<string, number>;
  orgTotalFlags: number;
  unresolvedThreePlusDisplay: number;
};

export const ReconFlagsSpotlightSummary = ({
  kpiCounts,
  orgTotalFlags,
  unresolvedThreePlusDisplay,
}: ReconFlagsSpotlightSummaryProps) => {
  const { t } = useTranslation(['reconciliation']);

  const spotlightTitle = (titleKey: string) => (
    <Typography
      component='span'
      variant='labelSmall'
      color='textSecondary'
      sx={SPOTLIGHT_TITLE_SX}
    >
      {t(titleKey)}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'grid',

          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },

          gap: 1.5,
        }}
      >
        {SPOTLIGHT_TYPES.map((spot) => (
          <SnapshotCard
            key={spot.type}
            title={spotlightTitle(spot.titleKey)}
            primaryValue={kpiCounts[spot.type]?.toLocaleString() ?? '—'}
            tone={spotlightColorKeyToSnapshotTone(spot.colorKey)}
            surface='accentBorder'
            compact
          />
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 1.5,
        }}
      >
        <SnapshotCard
          title={spotlightTitle('reconFlags.totalFlags')}
          primaryValue={orgTotalFlags.toLocaleString()}
          tone='neutral'
          surface='accentBorder'
          footer={t('reconFlags.totalFlagsHint')}
          compact
        />
        <SnapshotCard
          title={spotlightTitle('reconFlags.unresolvedThreePlus')}
          primaryValue={unresolvedThreePlusDisplay.toLocaleString()}
          tone='negative'
          surface='accentBorder'
          footer={t('reconFlags.unresolvedThreePlusHint')}
          compact
        />
      </Box>
    </Box>
  );
};
